import { Select } from '@pelckmans/business-components/components/select';
import { Fragment, useContext, useEffect, useState } from 'react';
import IdentityContext from '../../../../contexts/IdentityContext';
import { useTranslation } from 'react-i18next';
import { MediaContentType as ResponseMediaContentType } from '../../../../api/MediaContentTypeApi';
import { Medialink } from '../../_api/MedialinksApi';
import MaterialFiltersContext from '../../_contexts/MaterialFiltersContext';
import ModuleContext from '../../_contexts/ModuleContext';
import SharedMaterialContext from '../../_contexts/SharedMaterialContext';
import useBulkSharing from '../../_hooks/useBulkSharing';
import useDigibook from '../../_hooks/useDigibook';
import useMedialinks from '../../_hooks/useMedialinks';
import useSlideSets from '../../_hooks/useSlideSets';
import useUserMaterial from '../../_hooks/useUserMaterial';
import useWhitepages from '../../_hooks/useWhitepages';
import MediaContentTypesContext from '../contexts/MediaContentTypesContext';

export const MEDIA_CONTENT_TYPE_USER_MATERIAL = 'FICTIVE--USER-MATERIAL';
export const MEDIA_CONTENT_TYPE_SHARED_USER_MATERIAL = 'FICTIVE--SHARED-USER-MATERIAL';
export const MEDIA_CONTENT_TYPE_SLIDE_SETS = 'FICTIVE--SLIDE-SETS';
export const MEDIA_CONTENT_TYPE_WHITEPAGE = 'FICTIVE--WHITEPAGES';

type MediaContentType = Omit<ResponseMediaContentType, 'used'> & { used?: number };

type MixedMediaContextType = {
  category?: string;
  mediaContentTypes: MediaContentType[];
};

const transformMediaContentTypes = (
  mediaContentTypes: MediaContentType[],
  medialinks: Medialink[],
): MixedMediaContextType[] => {
  const categories = [...new Set(mediaContentTypes.map(mediaContentType => mediaContentType.category))];

  return categories.map(category => {
    const contentTypes = mediaContentTypes.filter(mediaContentType => mediaContentType.category === category);

    // Why is the data from the api wrong?
    contentTypes.forEach(contentType => {
      const amountOfMediaLinks = medialinks.filter(x => x.contentType.id === contentType.id).length;

      contentType.used = amountOfMediaLinks;
    });

    return {
      category,
      mediaContentTypes: contentTypes,
    };
  });
};

const printAmountOfMaterial = (amount: number) => ` (${amount})`;

export default function MediaContentTypesSelect({ onFilter }: { onFilter: (callback: () => void) => void }) {
  const [mixedMediaContentTypes, setMixedMediaContentTypes] = useState<MixedMediaContextType[]>();

  const { t } = useTranslation();
  const { digibook } = useDigibook();
  const { userMaterial } = useUserMaterial();
  const { whitepages } = useWhitepages();
  const { medialinks } = useMedialinks();
  const { slideSets } = useSlideSets();
  const { multiSelectModeEnabled, selectedUserMaterial, selectedMedialinks, setMultiSelectModeEnabled } =
    useBulkSharing();

  const { identity } = useContext(IdentityContext);
  const { selectedModule } = useContext(ModuleContext);
  const { sharedMaterial: sharedUserMaterial } = useContext(SharedMaterialContext);
  const { filterMaterialOnMediaContentType, mediaContentTypeId } = useContext(MaterialFiltersContext);
  const mediaContentTypes = useContext(MediaContentTypesContext);

  useEffect(() => {
    if (!mediaContentTypes || mediaContentTypes.length === 0) return;

    const _mixedMediaContentTypes = transformMediaContentTypes(mediaContentTypes, medialinks);

    setMixedMediaContentTypes(_mixedMediaContentTypes);
  }, [mediaContentTypes, medialinks]);

  const onSelectChange = (mediaContentType: string) => {
    if (multiSelectModeEnabled && (selectedUserMaterial.length > 0 || selectedMedialinks.length > 0)) {
      onFilter(() => filterMaterialOnMediaContentType(mediaContentType));
    } else {
      if (multiSelectModeEnabled) setMultiSelectModeEnabled(false);
      filterMaterialOnMediaContentType(mediaContentType);
    }
  };

  return (
    <div className="media-content-types-select">
      <Select
        id="media-content-types-select"
        name="media-content-type"
        onChange={onSelectChange}
        value={mediaContentTypeId}
      >
        <option value="">{t('MODULES.MATERIAL.BROWSE.FILTERS.CONTENT_TYPE.ALL_MEDIA_TYPES')}</option>

        {identity.isAuthenticated && (identity.isTeacherAlike || digibook) && (
          <option value={MEDIA_CONTENT_TYPE_USER_MATERIAL}>
            {t('MODULES.MATERIAL.BROWSE.FILTERS.CONTENT_TYPE.USER_MATERIAL')}
            {printAmountOfMaterial(userMaterial.length)}
          </option>
        )}

        {identity.isAuthenticated && !identity.isTeacherAlike && (
          <option value={MEDIA_CONTENT_TYPE_SHARED_USER_MATERIAL}>
            {t('MODULES.MATERIAL.BROWSE.FILTERS.CONTENT_TYPE.SHARED_USER_MATERIAL')}
            {printAmountOfMaterial(sharedUserMaterial.length)}
          </option>
        )}

        {identity.isTeacherAlike && digibook && (
          <option value={MEDIA_CONTENT_TYPE_WHITEPAGE}>
            {t('MODULES.MATERIAL.BROWSE.FILTERS.CONTENT_TYPE.WHITEPAGES')}
            {printAmountOfMaterial(whitepages.length)}
          </option>
        )}

        {identity.isTeacherAlike && selectedModule?.enableSlideSetsAccess && (
          <option value={MEDIA_CONTENT_TYPE_SLIDE_SETS}>
            {t('MODULES.MATERIAL.BROWSE.FILTERS.CONTENT_TYPE.SLIDE_SETS')}
            {printAmountOfMaterial(slideSets.length)}
          </option>
        )}

        {mixedMediaContentTypes?.map(({ category, mediaContentTypes }, idx) => (
          <Fragment key={idx}>
            {category && <option value={category}>{category}</option>}
            {mediaContentTypes.map(contentType => (
              <option key={contentType.id} value={contentType.id}>
                {contentType.category ? `- ${contentType.name}` : contentType.name}
                {printAmountOfMaterial(contentType.used || 0)}
              </option>
            ))}
          </Fragment>
        ))}
      </Select>
    </div>
  );
}
